import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar, FileText, AlertCircle } from 'lucide-react';
import AgendaWorkflow from './AgendaWorkflow';
import MinutesWorkflow from './MinutesWorkflow';
import { committeeMembers } from '../data/mockData';
import '../styles/committee-workflow.css';
import { useAuth } from '../AuthContext';
import API from '../services/api';

// Helper: derive a readable Firstname Lastname from email if names missing
function deriveNameFromEmail(email) {
  if (!email || typeof email !== 'string') return 'Unknown User';
  const local = email.split('@')[0];
  // Split on dots, underscores, hyphens
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length === 0) return email;
  return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
}

const CommitteeMeetingWorkflow = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  
  // Use URL params to persist workflow type and agenda across refreshes
  const workflowType = searchParams.get('type') || '';
  const urlAgendaId = searchParams.get('agendaId') || '';
  
  const setWorkflowType = (type) => {
    if (type) {
      setSearchParams({ type });
    } else {
      setSearchParams({});
    }
  };
  
  const setUrlAgendaId = (agendaId) => {
    const params = { type: workflowType };
    if (agendaId) {
      params.agendaId = agendaId;
    }
    setSearchParams(params);
  };
  
  // Derive a display user object from auth context (fallbacks for missing fields)
  const displayUser = {
    name: profile
      ? `${profile.FIRST_NAME || ''} ${profile.LAST_NAME || ''}`.trim() || deriveNameFromEmail(profile.USERNAME || user?.username)
      : (user
          ? (user.firstName || user.lastName
              ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
              : deriveNameFromEmail(user.username))
          : 'Guest'),
    role: user?.role || 'Unknown Role',
    isCommitteeMember: true
  };

  // Fetch profile record (FIRST_NAME, LAST_NAME) once authenticated user exists
  useEffect(() => {
    if (!user || !user.username) return;
    let cancelled = false;
    async function loadProfile(){
      setProfileLoading(true); setProfileError(null);
      try {
        const res = await API.get('/api/profile/profile');
        if(!cancelled){
          setProfile(res.data);
        }
      } catch(e){
        if(!cancelled){
          setProfileError(e.message || 'Failed to load profile');
        }
      } finally {
        if(!cancelled){
          setProfileLoading(false);
        }
      }
    }
    loadProfile();
    return () => { cancelled = true; };
  }, [user]);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    const now = new Date();
    setFormData(prev => ({
      ...prev,
      initiator: displayUser.name,
      department: displayUser.role,
      date: now.toISOString().split('T')[0],
      time: now.toTimeString().slice(0, 5)
    }));
  }, [displayUser.name, displayUser.role]);

  return (
    <div className="workflow-container">
      <div className="workflow-card">
        <div className="workflow-header">
          <div>
            <h1 className="workflow-title">Committee Meetings</h1>
            <p className="workflow-subtitle">Health & Safety Committee Documentation</p>
          </div>
          <div className="user-info">
            <p className="user-label">Logged in as:</p>
            <p className="user-name">{displayUser.name}{profileLoading ? ' (loading...)' : ''}</p>
            <p className="user-role">{displayUser.role}</p>
            {profileError && <p style={{color:'#b91c1c', fontSize:'12px'}}>Profile error: {profileError}</p>}
          </div>
        </div>

        {!workflowType ? (
          <div className="workflow-selection">
            <div className="info-banner">
              <h2 className="info-title">About This Workflow</h2>
              <p className="info-text">
                This workflow is used to document committee meetings, capture acknowledgment 
                of understanding from committee members, and streamline the process for sharing 
                meeting minutes with staff.
              </p>
            </div>

            <div className="workflow-options">
              <button
                onClick={() => setWorkflowType('agenda')}
                className="workflow-option agenda-option"
              >
                <div className="option-header">
                  <Calendar className="option-icon" />
                  <h3 className="option-title">Create Committee Agenda</h3>
                </div>
                <p className="option-description">
                  Initiate a new committee meeting agenda and notify members for attendance confirmation
                </p>
                <p className="option-note">
                  ✓ Only for Health & Safety Advisor or Committee Member Delegates
                </p>
              </button>

              <button
                onClick={() => setWorkflowType('minutes')}
                className="workflow-option minutes-option"
              >
                <div className="option-header">
                  <FileText className="option-icon" />
                  <h3 className="option-title">Record Meeting Minutes</h3>
                </div>
                <p className="option-description">
                  Document committee meeting activities, decisions, and action items
                </p>
                <p className="option-note">
                  ✓ Available to all Committee Members
                </p>
              </button>
            </div>

            <div className="usage-info">
              <h3 className="usage-title">
                <AlertCircle className="usage-icon" />
                When to use this workflow:
              </h3>
              <ul className="usage-list">
                <li>• Committee members reviewing health and safety topics at scheduled meetings</li>
                <li>• Documenting special meetings due to serious incidents or concerns</li>
                <li>• Recording unresolved work refusal situations</li>
              </ul>
            </div>
          </div>
        ) : (
          <div>
            <button
              onClick={() => setWorkflowType('')}
              className="back-button"
            >
              ← Back to Workflow Selection
            </button>
            
            <div className="form-header-info">
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Initiator:</span>
                  <span className="info-value">{formData.initiator}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Department:</span>
                  <span className="info-value">{formData.department}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Date:</span>
                  <span className="info-value">{formData.date}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Time:</span>
                  <span className="info-value">{formData.time}</span>
                </div>
              </div>
            </div>

            {workflowType === 'agenda' ? (
              <AgendaWorkflow 
                formData={formData} 
                setFormData={setFormData}
                committeeMembers={committeeMembers}
              />
            ) : (
              <MinutesWorkflow 
                formData={formData} 
                setFormData={setFormData}
                committeeMembers={committeeMembers}
                initialAgendaId={urlAgendaId}
                onAgendaChange={setUrlAgendaId}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommitteeMeetingWorkflow;